import React, { useEffect, useRef, useState, useCallback } from 'react';
import { noise1D, pointsToSmoothPath, WIGGLE_TIME_FREQ, WIGGLE_SPATIAL_FREQ } from '../utils/wiggle';

const HIGHLIGHT_AMPLITUDE = 3;
const POINT_SPACING = 12;
const STROKE_COLOR = '#FFD600';
const STROKE_WIDTH = 26;

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const WigglyHighlight = () => {
  const svgRef = useRef(null);
  const rafRef = useRef(null);
  const phaseSeedRef = useRef(Math.random() * 1000);
  const [pathD, setPathD] = useState('');
  const widthRef = useRef(0);

  const buildBasePoints = useCallback((width) => {
    const cap = STROKE_WIDTH / 2;
    const insetStart = cap;
    const insetEnd = width - cap;
    if (insetEnd <= insetStart) {
      return [{ x: width / 2, y: 0 }];
    }
    const span = insetEnd - insetStart;
    const count = Math.max(2, Math.ceil(span / POINT_SPACING));
    const pts = [];
    for (let i = 0; i <= count; i++) {
      pts.push({ x: insetStart + i * (span / count), y: 0 });
    }
    return pts;
  }, []);

  const perturb = useCallback((basePoints, t) => {
    const seed = phaseSeedRef.current;
    return basePoints.map((p, i) => {
      const n = noise1D(i * WIGGLE_SPATIAL_FREQ + t * WIGGLE_TIME_FREQ + seed);
      return { x: p.x, y: p.y + n * HIGHLIGHT_AMPLITUDE };
    });
  }, []);

  const tick = useCallback(() => {
    const w = widthRef.current;
    if (w <= 0) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const t = performance.now() / 1000;
    const base = buildBasePoints(w);
    const perturbed = perturb(base, t);
    setPathD(pointsToSmoothPath(perturbed));

    if (!prefersReducedMotion) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [buildBasePoints, perturb]);

  useEffect(() => {
    const measure = () => {
      const parent = svgRef.current?.parentElement;
      if (parent) {
        widthRef.current = parent.offsetWidth;
      }
    };

    measure();

    const ro = new ResizeObserver(measure);
    const parent = svgRef.current?.parentElement;
    if (parent) ro.observe(parent);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [tick]);

  const padding = HIGHLIGHT_AMPLITUDE + STROKE_WIDTH / 2;

  return (
    <svg
      ref={svgRef}
      style={{
        position: 'absolute',
        left: '-2px',
        right: '-2px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '100%',
        height: padding * 2,
        overflow: 'visible',
        pointerEvents: 'none',
        mixBlendMode: 'multiply',
        zIndex: 1,
      }}
      viewBox={`0 ${-padding} ${widthRef.current || 1} ${padding * 2}`}
      preserveAspectRatio="none"
    >
      <path
        d={pathD}
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

export default WigglyHighlight;
